import {GameView} from "./GameView";
import {PixiAssetsLoader} from "../assets/PixiAssetsLoader";
import {GameObject} from "./GameObject";
import {ViewUtils} from "../utils/ViewUtils";
import * as PIXI from 'pixi.js';
import {Movie} from "../flump/display/Movie";
import {Container} from "../flump/display/Container";
import {Placer} from "../flump/display/Placer";
import {Easing} from "@tweenjs/tween.js";
import {SoundManager} from "../../managers/SoundManager";

export class GameDialog extends GameView {

  static SHOW_TIME: number = 0.6;

  public assetsLoader: PixiAssetsLoader | null = null;

  protected _waiter: Movie | null = null;
  protected _dialog: GameView | null = null;
  protected _blackBack: PIXI.Graphics | null = null;
  protected _dialogContent: Container | null = null;
  protected _isInHideProcess: boolean = false;
  public _shouldDisposeCallback?: ((dialog: GameDialog) => void) | null; // internal, used in App, don't assign explicitly

  get btnClose() {
    return this._dialogContent?.getButtonByLabel('btn_close');
  }

  protected get title(): string | null {
    return null;
  }

  protected get dialogContentMoldName(): string {
    return '';
  }

  protected get dialogScale(): number {
    let multiplier = 1;
    if (this.root.app.device.isTablet || this.root.app.device.isDesktop) {
      multiplier = this.tabletMultiplier;
    }
    if (this.root.app.device.isIOS) {
      return 1.3 * multiplier;
    }
    if (this.root.app.device.isAndroid) {
      return 1.2 * multiplier;
    }
    return multiplier;
  }

  protected get tabletMultiplier(): number {
    return 0.8;
  }

  protected get backgroundAlpha(): number {
    return 0.5;
  }

  protected get createDialogLib() {
    return this.root.uiLib;
  }

  protected playShowSound(): void {
    void this.root.app.sound.playSound(SoundManager.DialogOpen);
  }

  async loadAssets(onLoaded?: () => void) {
    if (onLoaded) {
      onLoaded();
    }
  }

  onAdded(): void {
    this.createBlackBack();
    this.createBackgroundEffect();
  }

  override destroy() {
    this._dialog = GameObject.destroy(this._dialog);
    this._blackBack = GameObject.destroy(this._blackBack);
    this.assetsLoader = GameObject.destroy(this.assetsLoader);
    this.hideWaiter();
    this._dialogContent = GameObject.destroy(this._dialogContent);
    this._shouldDisposeCallback = null;
    super.destroy();
  }

  showWaiter(): void {
    this.hideWaiter();
    this._waiter = this.root.uiLib!.create("loading_anim") as Movie;
    ViewUtils.fitToHeight(this._waiter, this.root.adapt(150), true, true);
    ViewUtils.alignPivot(this._waiter);
    this._waiter.x = this.root.fixedStageWidth / 2;
    this._waiter.y = this.root.fixedStageHeight / 2;
    this._waiter.playLoop();
    this.addChildAt(this._waiter, this.children?.length);
  }

  hideWaiter(): void {
    if (this._waiter) {
      this._waiter = GameObject.destroy(this._waiter);
    }
  }

  createDialog(): void {
    this._dialog = new GameView();
    this.addChild(this._dialog);

    this._dialogContent = this.createDialogLib!.create(this.dialogContentMoldName) as Container;
    this._dialog.addChild(this._dialogContent);

    if (this.btnClose) {
      this.btnClose.clickCallback = this.onButtonClose.bind(this);
    }
    this.createTitle();
  }

  show(): void {
    this.playShowSound();

    if (this._dialog) {
      this._dialog.interactive = false;
      this._dialog.scale = this.dialogScale;

      ViewUtils.fitToHeight(this._dialog, this.root.fixedStageSafeHeight - this.root.adapt(100), true, false);
      ViewUtils.alignPivot(this._dialog);

      this._dialog.x = this.getCenterX();
      this._dialog.y = -this._dialog.height;

      this.root.juggler.tween(this._dialog, GameDialog.SHOW_TIME, {
        y: this.getCenterY(),
      }).easing(Easing.Back.Out).onComplete(this.onDialogShowEnd.bind(this)).start();
    }
  }

  hide(): void {
    if (this._isInHideProcess) {
      return;
    }
    this._isInHideProcess = true;
    this.interactive = false;
    this.playCloseSound();
    this.hideBlackBack();
    this.hideDialog();
  }

  onButtonClose(): void {
    this.interactive = false;
    void this.root.app.sound.playSound(SoundManager.Click);
    this.hide();
  }

  protected createBlackBack(): void {
    this._blackBack = ViewUtils.createRect(this.root.fixedStageWidth, this.root.fixedStageHeight, "0x000000");
    this.addChild(this._blackBack);
    this._blackBack.interactive = true; // !!! keep


    this.root.juggler.tween(this._blackBack, 0.5 * GameDialog.SHOW_TIME, {
      alpha: this.backgroundAlpha
    }).start();
  }

  protected createBackgroundEffect(): void {
    // No implementation
  }

  protected createTitle(): void {
    if (this.title) {
      const titlePlacer: Placer = this._dialogContent!.getPlacerByLabel('txt_title');
      titlePlacer.place(this.root.dialogTitleTextLib.createBitmapText(this.title, titlePlacer.width, titlePlacer.height))
        .fitHeight(true, true)
        .fitWidth(true, false)
        .alignCenter();
    }
  }

  protected getCenterX(): number {
    return this.root.fixedStageWidth / 2;
  }

  protected getCenterY(): number {
    return this.root.safeZoneTop + this.root.fixedStageSafeHeight / 2;
  }

  protected hideDialog(): void {
    if (this._dialog) {
      this._dialog.interactive = false;

      this.root.juggler.tween(this._dialog, GameDialog.SHOW_TIME, {
        delay: 0.1,
        y: -this._dialog.height,
      }).easing(Easing.Back.In).onComplete(this.disposeFromNavigator.bind(this)).start();
    } else {
      this.disposeFromNavigator();
    }
  }

  protected onDialogShowEnd(): void {
    if (this._dialog) {
      this._dialog.interactive = true;
    }
  }

  protected playCloseSound(): void {
    void this.root.app.sound.playSound(SoundManager.DialogClose);
  }

  protected disposeFromNavigator(): void {
    if (this._shouldDisposeCallback) {
      this._shouldDisposeCallback(this);
    }
  }

  protected hideBlackBack(): void {
    if (this._blackBack) {
      this.root.juggler.tween(this._blackBack, 0.5 * GameDialog.SHOW_TIME, {
        alpha: 0
      }).start();
    }
  }
}
